import { ButtonType } from "@components/atoms/Button/types";
import React, { useEffect, useState } from "react";
import { Button, Spacer, VDBText } from "@components/atoms";
import styled, { DefaultTheme } from "styled-components/native";
import { SpaceType } from "@components/atoms/Spacer/types";
import { useTheme } from "styled-components/native";
import { useRoute } from "@react-navigation/native";
import { getRequestType } from "./utils";
import Modal from "@components/atoms/Modal";
import RequestChat from "./RequestChat";
import RequestConfirmPopup from "./RequestConfirmPopup";
import { ActiveClickTypes } from "../types";
import { useTranslation } from "react-i18next";
import { VDBIcons } from "@utils/VDBIcons";
import { StockItemRequestClass } from "@src/types/HistoryRequest";
import { useTypographyStore } from "@redux/slices/typographySlice";
import { View } from "react-native";
import { css } from "styled-components/native";
import { useSafeAreaInsets } from "react-native-safe-area-context";

type ButtonsContainerProps = {
  stockItemRequest: StockItemRequestClass;
  productType: any;
  onSubmitChange: () => void;
  modifyRequest: any;
  disabledButton?: boolean;
  selectedItems?: any;
  isHideOtherButtonVisible?: boolean;
  isBuilderItem?: boolean;
};

const ButtonsContainer = ({
  stockItemRequest,
  productType,
  onSubmitChange,
  modifyRequest,
  disabledButton = false,
  isHideOtherButtonVisible,
  isBuilderItem,
}: ButtonsContainerProps) => {
  const theme = useTheme();
  const typography = useTypographyStore();
  const { t } = useTranslation();
  const requestType = getRequestType(stockItemRequest.request_type);
  const route = useRoute();
  const [openChat, setOpenChat] = useState(route.params?.openChat ?? false);
  const [showMessageModal, setShowMessageModal] = useState(false);
  const [isDisabledButton, setDisabledButton] = useState(disabledButton);
  const { bottom } = useSafeAreaInsets();
  let recipientEmailAvailable =
    !stockItemRequest?.quote_details?.is_user_registered &&
    stockItemRequest.view_type === "buyer_view";

  let showChatText =
    requestType === "Quote" && stockItemRequest.view_type === "buyer_view";

  useEffect(() => {
    setDisabledButton(disabledButton);
  }, [disabledButton]);

  const [showPopup, setShowPopup] = useState(false);
  const [activeClick, setActiveClick] = useState<ActiveClickTypes>(
    ActiveClickTypes.DECLINE
  );
  const buttonWidth = theme.isMobile ? "auto" : theme.isLargeScreen ? 224 : 160;

  const showConfirmPopup = (click: ActiveClickTypes) => {
    setShowPopup(true);
    setActiveClick(click);
  };

  useEffect(() => {
    setOpenChat(route?.params?.openChat ?? false);
  }, [route?.params]);

  useEffect(() => {
    if (openChat) {
      setShowMessageModal(true);
    }
  }, [openChat]);

  const DeclineButton = (declineProps: any) => {
    const { type } = declineProps;
    return (
      <Button
        type={type || ButtonType.primary}
        width={buttonWidth}
        category={productType.theme}
        title={t("requestHistoryDetail.buttons.decline.text")}
        disabled={isDisabledButton}
        onPress={() => {
          showConfirmPopup(ActiveClickTypes.DECLINE);
        }}
      />
    );
  };

  const WithdrawButton = (declineProps: any) => {
    const { type } = declineProps;
    return (
      <Button
        type={type || ButtonType.secondary}
        width={buttonWidth}
        category={productType.theme}
        title={t("requestHistoryDetail.buttons.withdraw.text")}
        disabled={isDisabledButton}
        onPress={() => {
          showConfirmPopup(ActiveClickTypes.WITHDRAW);
        }}
      />
    );
  };

  const AcceptButton = (acceptProps: any) => {
    const { type } = acceptProps;
    return (
      <Button
        type={type || ButtonType.primary}
        width={buttonWidth}
        category={productType.theme}
        disabled={isDisabledButton}
        title={
          recipientEmailAvailable
            ? t("requestHistoryDetail.buttons.acceptBehalf.text")
            : t("requestHistoryDetail.buttons.accept.text")
        }
        onPress={() => {
          showConfirmPopup(
            requestType === "Quote"
              ? ActiveClickTypes.ACCEPTQUOTE
              : ActiveClickTypes.ACCEPT
          );
        }}
      />
    );
  };

  const ModifyButton = (modifyProps: any) => {
    const { type, modifyRequest: onModifyRequest } = modifyProps;
    return (
      <Button
        type={type || ButtonType.tertiary}
        width={buttonWidth}
        category={productType.theme}
        title={t("requestHistoryDetail.buttons.modify.text")}
        onPress={onModifyRequest}
      />
    );
  };

  const MessageButton = (messageProps: any) => {
    const { type, customText } = messageProps;
    return (
      <Button
        type={type ?? ButtonType.secondary}
        width={buttonWidth}
        category={productType.theme}
        showIcon={true}
        hideText={true}
        showLeftText={true}
        iconSource={VDBIcons.MESSAGE}
        iconColor={
          type === ButtonType.primary
            ? (theme[`${productType.theme}-05` as keyof DefaultTheme] as string)
            : (theme[`${productType.theme}-06` as keyof DefaultTheme] as string)
        }
        title={t(
          customText ? customText : "requestHistoryDetail.buttons.message.text"
        )}
        onPress={() => {
          setShowMessageModal(true);
        }}
      />
    );
  };

  const ButtonContent = (
    <>
      <ButtonContainer>
        <ButtonFlexContainer>
          <AcceptButton type={ButtonType.primary} />
        </ButtonFlexContainer>
      </ButtonContainer>
      <Spacer size={SpaceType.Big} />
    </>
  );

  const Wrapper = theme.isMobile ? View : ButtonFlexContainer;
  const bottomPadding = theme.isMobile ? bottom : SpaceType.Big;
  return (
    <>
      {stockItemRequest.view_type === "buyer_view" ? (
        requestType === "Buy" ? (
          <ButtonView paddingBottom={bottomPadding}>
            <ButtonContainer
              justifyContent={
                stockItemRequest.status === "pending"
                  ? "space-between"
                  : "center"
              }
              width={"100%"}
            >
              {stockItemRequest.status === "pending" && !isBuilderItem && (
                <ButtonFlexContainer>
                  <ModifyButton
                    type={ButtonType.tertiary}
                    modifyRequest={modifyRequest}
                  />
                </ButtonFlexContainer>
              )}

              {stockItemRequest.status === "pending" && isBuilderItem && (
                <ButtonFlexContainer>
                  <WithdrawButton
                    type={ButtonType.tertiary}
                    modifyRequest={modifyRequest}
                  />
                </ButtonFlexContainer>
              )}
              <ButtonFlexContainer>
                <MessageButton type={ButtonType.primary} />
              </ButtonFlexContainer>
            </ButtonContainer>
          </ButtonView>
        ) : requestType === "Memo" ? (
          <ButtonView paddingBottom={bottomPadding}>
            <ButtonContainer
              justifyContent={
                stockItemRequest.status === "pending"
                  ? "space-between"
                  : "center"
              }
              width={"100%"}
            >
              {stockItemRequest.status === "pending" && (
                <ButtonFlexContainer>
                  <ModifyButton
                    type={ButtonType.tertiary}
                    modifyRequest={modifyRequest}
                  />
                </ButtonFlexContainer>
              )}
              <ButtonFlexContainer>
                <MessageButton type={ButtonType.primary} />
              </ButtonFlexContainer>
            </ButtonContainer>
          </ButtonView>
        ) : requestType === "Hold" ? (
          <ButtonView paddingBottom={bottomPadding}>
            <ButtonContainer
              justifyContent={
                stockItemRequest.status === "pending"
                  ? "space-between"
                  : "center"
              }
              width={"100%"}
            >
              {stockItemRequest.status === "pending" && (
                <ButtonFlexContainer>
                  <ModifyButton
                    type={ButtonType.tertiary}
                    modifyRequest={modifyRequest}
                  />
                </ButtonFlexContainer>
              )}
              <ButtonFlexContainer>
                <MessageButton type={ButtonType.primary} />
              </ButtonFlexContainer>
            </ButtonContainer>
          </ButtonView>
        ) : requestType === "Quote" ? (
          <ButtonQuoteView paddingBottom={bottomPadding}>
            <ButtonContainerView
              justifyContent={
                stockItemRequest.status === "pending" &&
                !isHideOtherButtonVisible
                  ? "space-between"
                  : theme.isLargeScreen
                  ? "center"
                  : "space-evenly"
              }
            >
              <Wrapper>
                {stockItemRequest.status === "pending" &&
                  !isHideOtherButtonVisible &&
                  recipientEmailAvailable &&
                  (theme.isMobile ? (
                    ButtonContent
                  ) : (
                    <ButtonFlexContainer>{ButtonContent}</ButtonFlexContainer>
                  ))}
              </Wrapper>
              <ButtonContainer
                justifyContent={
                  stockItemRequest.status === "pending" &&
                  !isHideOtherButtonVisible
                    ? "space-between"
                    : theme.isLargeScreen
                    ? "center"
                    : "space-evenly"
                }
                allowFlex={
                  recipientEmailAvailable || theme.isMobile ? false : true
                }
              >
                {stockItemRequest.status === "pending" &&
                  !isHideOtherButtonVisible && (
                    <ButtonFlexContainer>
                      <WithdrawButton
                        type={ButtonType.tertiary}
                        modifyRequest={modifyRequest}
                      />
                    </ButtonFlexContainer>
                  )}
                <ButtonFlexContainer>
                  <MessageButton
                    type={ButtonType.secondary}
                    customText={
                      showChatText
                        ? "requestHistoryDetail.buttons.chat.text"
                        : undefined
                    }
                  />
                </ButtonFlexContainer>
              </ButtonContainer>
            </ButtonContainerView>
            {stockItemRequest.status === "pending" &&
              !isHideOtherButtonVisible &&
              recipientEmailAvailable && (
                <>
                  {theme.isMobile && <Spacer size={SpaceType.Big} />}
                  <VDBText
                    type={typography["Minor-R"]}
                    color={theme["core-06"]}
                  >
                    {t("requestHistoryDetail.acceptBehalf.screenText.text")}
                  </VDBText>
                </>
              )}
          </ButtonQuoteView>
        ) : (
          <ButtonView paddingBottom={bottomPadding}>
            <ButtonContainer justifyContent={"center"} width={"100%"}>
              <ButtonFlexContainer>
                <MessageButton type={ButtonType.primary} />
              </ButtonFlexContainer>
            </ButtonContainer>
          </ButtonView>
        )
      ) : stockItemRequest.view_type === "seller_view" ? (
        ["Buy", "Memo", "Hold", "Questions"].includes(requestType) ? (
          <ButtonView
            justifyContent={
              stockItemRequest.status === "pending"
                ? "space-between"
                : "space-evenly"
            }
            paddingBottom={bottomPadding}
          >
            {stockItemRequest.status === "pending" && (
              <>
                <ButtonContainer>
                  <ButtonFlexContainer>
                    <AcceptButton type={ButtonType.primary} />
                  </ButtonFlexContainer>
                </ButtonContainer>
                <Spacer size={SpaceType.Big} />
              </>
            )}
            <ButtonContainer
              justifyContent={
                stockItemRequest.status === "pending"
                  ? "space-between"
                  : theme.isLargeScreen
                  ? "center"
                  : "space-evenly"
              }
            >
              {stockItemRequest.status === "pending" &&
                !isHideOtherButtonVisible && (
                  <ButtonFlexContainer>
                    <DeclineButton type={ButtonType.tertiary} />
                  </ButtonFlexContainer>
                )}
              <ButtonFlexContainer>
                <MessageButton
                  type={
                    stockItemRequest.status === "pending"
                      ? ButtonType.secondary
                      : ButtonType.primary
                  }
                />
              </ButtonFlexContainer>
            </ButtonContainer>
          </ButtonView>
        ) : requestType === "Quote" ? (
          <ButtonView
            justifyContent={
              stockItemRequest.status === "pending" && !isHideOtherButtonVisible
                ? "space-between"
                : "space-evenly"
            }
            paddingBottom={bottomPadding}
          >
            {stockItemRequest.status === "pending" &&
              !isHideOtherButtonVisible && (
                <>
                  <ButtonContainer>
                    <ButtonFlexContainer>
                      <AcceptButton type={ButtonType.primary} />
                    </ButtonFlexContainer>
                  </ButtonContainer>
                  <Spacer size={SpaceType.Big} />
                </>
              )}

            <ButtonContainer
              justifyContent={
                stockItemRequest.status === "pending"
                  ? "space-between"
                  : theme.isLargeScreen
                  ? "center"
                  : "space-evenly"
              }
            >
              {stockItemRequest.status === "pending" &&
                !isHideOtherButtonVisible && (
                  <ButtonFlexContainer>
                    <DeclineButton
                      type={ButtonType.tertiary}
                      modifyRequest={modifyRequest}
                    />
                  </ButtonFlexContainer>
                )}
              <ButtonFlexContainer>
                <MessageButton type={ButtonType.secondary} />
              </ButtonFlexContainer>
            </ButtonContainer>
          </ButtonView>
        ) : (
          <></>
        )
      ) : (
        <></>
      )}
      {showPopup && (
        <RequestConfirmPopup
          stockItemRequest={stockItemRequest}
          activeClick={activeClick}
          onPopupShowChange={setShowPopup}
          onSubmitChange={onSubmitChange}
        />
      )}
      {showMessageModal && (
        <Modal
          headerTitle={t("requestHistoryDetail.requestChat.modal.title.text")}
          isOpen={showMessageModal}
          onClose={() => {
            setShowMessageModal(false);
            setOpenChat(false);
          }}
          paddingLeft={0}
          paddingRight={0}
          paddingTop={0}
          paddingBottom={0}
          skipKeyboardAware={true}
          size={"md"}
        >
          <RequestChat stockItemRequest={stockItemRequest} />
        </Modal>
      )}
    </>
  );
};

const ButtonView = styled.View<{
  justifyContent?: string;
  paddingBottom?: number;
}>`
  padding: ${({ paddingBottom = 0 }) =>
    css`
      ${SpaceType.Basic}px ${SpaceType.Double}px ${paddingBottom}px ${SpaceType.Double}px
    `};
  background-color: ${({ theme }) => theme["core-01"]};
  flex-direction: ${({ theme }) => (theme.isMobile ? "column" : "row-reverse")};
  justify-content: ${({ justifyContent }) => justifyContent ?? "space-evenly"};
  border-radius: ${({ theme }) => (theme.isMobile ? "0" : "8px")};
`;

const ButtonContainerView = styled.View<{ justifyContent?: string }>`
  flex-direction: ${({ theme }) => (theme.isMobile ? "column" : "row-reverse")};
  justify-content: ${({ justifyContent }) => justifyContent ?? "space-evenly"};
  border-radius: ${({ theme }) => (theme.isMobile ? "0" : "8px")};
`;

const ButtonQuoteView = styled.View<{
  justifyContent?: string;
  paddingBottom?: number;
}>`
  padding: ${({ paddingBottom = 0 }) =>
    `${SpaceType.Big}px ${SpaceType.Double}px ${paddingBottom}px ${SpaceType.Double}px`};
  background-color: ${({ theme }) => theme["core-01"]};
  flex-direction: "column";
`;

const ButtonContainer = styled.View<{
  justifyContent?: string;
  width?: string;
  allowFlex?: boolean;
}>`
  flex-direction: row;
  ${({ allowFlex }) =>
    allowFlex &&
    css`
      flex: 1;
    `};
  justify-content: ${({ justifyContent }) => justifyContent ?? "space-between"};
  width: ${({ width }) => width ?? "unset"};
  gap: ${SpaceType.Basic}px;
`;

const ButtonFlexContainer = styled.View`
  flex: ${({ theme }) => (theme.isMobile ? "1" : "none")};
`;

export default ButtonsContainer;
